import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import { X, HelpCircle, Brain } from "lucide-react";

const StartResearchDialog = ({
  open,
  onClose,
  onSubmit,
  loading,
  error,
}: any) => {
  const [modelAPIKey, setModelAPIKey] = useState("");
  const [showHelp, setShowHelp] = useState(false);

  const handleSubmit = () => {
    onSubmit({
      modelAPIKey,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        },
      }}
    >
      {/* Custom header with gradient background */}
      <Box
        sx={{
          position: "relative",
          background: "linear-gradient(to right, #E11D48, #F43F5E)",
          padding: "20px 24px",
          color: "white",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
        >
          <X size={18} />
        </IconButton>

        <Typography variant="h5" sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
          Start ICP Research
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          *Configure AI model selection settings for generating your ideal
          customer profile
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mt: 2, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              AI Model API Key Configuration
            </Typography>
            <IconButton size="small" onClick={() => setShowHelp(!showHelp)}>
              <HelpCircle size={16} color="#E11D48" />
            </IconButton>
          </Box>

          {showHelp && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: "#FEF2F2",
                borderRadius: "8px",
                border: "1px solid #FECACA",
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>AI Model API Key Information:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                The system supports keys from:
              </Typography>
              <ul style={{ marginLeft: "20px", marginBottom: "8px" }}>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    OpenAI (GPT models)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Anthropic (Claude models)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    DeepSeek
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Google (Gemini models)
                  </Typography>
                </li>
              </ul>
              <Typography variant="body2" color="text.secondary">
                Your selected model's API Key is required.
              </Typography>
            </Box>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: "8px",
                "& .MuiAlert-icon": {
                  color: "#EF4444",
                },
              }}
            >
              {error}
            </Alert>
          )}
        </Box>

        <TextField
          fullWidth
          label="Model API Key"
          value={modelAPIKey}
          onChange={(e) => setModelAPIKey(e.target.value)}
          placeholder="Enter your Model API Key (required)"
          helperText="This should be available within your selected model's hosted platform/console settings"
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 0,
          display: "flex",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "8px",
            color: "#374151",
            fontWeight: 500,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={<Brain className="h-4 w-4" />}
          sx={{
            borderRadius: "8px",
            background: "linear-gradient(to right, #E11D48, #F43F5E)",
            textTransform: "none",
            fontWeight: 500,
            boxShadow: "0 4px 6px -1px rgba(225, 29, 72, 0.2)",
            "&:hover": {
              background: "linear-gradient(to right, #BE123C, #E11D48)",
            },
            "&.Mui-disabled": {
              background: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {loading ? "Starting Research..." : "Start Research"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartResearchDialog;
